import React, { useContext, useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { Button, InputNumber, Space, Table, Form, Modal, Checkbox } from 'antd'
import client from 'apollo'
import { AppContext } from 'AppContext'
import { GET_OPEN_POSITION_FOR_CLIENT } from './graphql/Queries'
import Page404 from 'components/Page404'
import Loader from 'components/loaders/Loader'
import EditPencilIcon from 'assets/images/new-images/edit-pencil-icon.svg'
import './index.css'
import openNotification from 'utils/Notification'
import { UPDATE_OPEN_POSITION_FOR_CLIENT } from './graphql/Mutation'
import CustomMeta from 'components/CustomMeta'
import { canUserEdit } from 'utils/User'

const UpdatePosition = () => {
  const {
    state: {
      currentUser,
      isClient,
      userPermissions,
    },
  } = useContext(AppContext)
  const { userName, strategyCode } = currentUser || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(false)
  const [data, setData] = useState([])
  const [originalData, setOriginalData] = useState([])
  const [currentPositions, setCurrentPositions] = useState([])
  const [editableRows, setEditableRows] = useState(new Set())
  const [activeEditingIndex, setActiveEditingIndex] = useState(null)
  const [error, setError] = useState({})
  const [form] = Form.useForm()

  // Verification modal states
  const [isVerifyModalVisible, setIsVerifyModalVisible] = useState(false)
  const [changedRows, setChangedRows] = useState([])
  const [isAgreed, setIsAgreed] = useState(false)

  const getSpDataForCurrentPosition = () => {
    setLoading(true)
    client
      .query({
        query: GET_OPEN_POSITION_FOR_CLIENT,
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        const positions = (res?.data?.spGetCurrentPosition[0] || []).map(
          (pos) => ({
            ...pos,
            Qty: Number(pos.Qty !== undefined && pos.Qty !== null ? pos.Qty : 0),
          })
        )
        setData(positions)
        setOriginalData(positions)
        setCurrentPositions(positions)
        setEditableRows(new Set())
        setActiveEditingIndex(null)
        setChangedRows([])
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getSpDataForCurrentPosition()
  }, []) // eslint-disable-line

  const handleValuesChange = (changedValues) => {
    if (changedValues?.positions) {
      setCurrentPositions((prev) => {
        const next = [...prev]
        Object.keys(changedValues.positions).forEach((key) => {
          const idx = Number(key)
          const changedRow = changedValues.positions[key]
          if (next[idx] && changedRow && changedRow.Qty !== undefined) {
            next[idx] = {
              ...next[idx],
              Qty: changedRow.Qty,
            }
          }
        })
        return next
      })
    }
  }

  const handleEdit = (index) => {
    setActiveEditingIndex(index)
    const existingQty =
      currentPositions[index]?.Qty !== undefined
        ? currentPositions[index]?.Qty
        : data[index]?.Qty !== undefined
        ? data[index]?.Qty
        : originalData[index]?.Qty

    if (existingQty !== undefined) {
      form.setFields([
        {
          name: ['positions', index, 'Qty'],
          value: existingQty,
        },
      ])
    }

    setEditableRows((prev) => {
      const next = new Set(prev)
      next.add(index)
      return next
    })
  }

  const getIsEdited = (index, record) => {
    const originalQty = originalData[index]?.Qty
    const currentQty =
      currentPositions[index]?.Qty !== undefined
        ? currentPositions[index]?.Qty
        : record?.Qty
    if (originalQty === undefined || currentQty === undefined) return false
    return Number(currentQty) !== Number(originalQty)
  }

  const handleMainSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const changed = []
        data.forEach((prevRow, index) => {
          if (!editableRows.has(index)) return

          const formQty = values?.positions?.[index]?.Qty
          const currentQty =
            formQty !== undefined
              ? formQty
              : currentPositions[index]?.Qty !== undefined
              ? currentPositions[index]?.Qty
              : form.getFieldValue(['positions', index, 'Qty'])

          if (
            currentQty !== undefined &&
            currentQty !== null &&
            currentQty !== '' &&
            Number(currentQty) !== Number(prevRow?.Qty || 0)
          ) {
            changed.push({
              ...prevRow,
              ClientCode: prevRow?.ClientCode || '',
              Script: prevRow?.Script || '',
              beforeQty: prevRow?.Qty !== undefined ? prevRow.Qty : 0,
              afterQty: Number(currentQty),
              Rate:
                prevRow?.Rate !== undefined && prevRow?.Rate !== null
                  ? prevRow.Rate
                  : 0,
              originalIndex: index,
            })
          }
        })

        if (!changed.length) {
          openNotification('info', 'No changes found to update')
          return
        }

        setChangedRows(changed)
        setIsAgreed(false)
        setIsVerifyModalVisible(true)
      })
      .catch((err) => {
        console.error('Validation error:', err)
      })
  }

  const handleCancelVerification = () => {
    setIsVerifyModalVisible(false)
    setIsAgreed(false)
  }

  const handleConfirmUpdate = () => {
    if (!isAgreed) return
    setDataLoading(true)

    const payloadRows = changedRows.map((row) => ({
      ClientCode: row.ClientCode || '',
      Script: row.Script || '',
      Qty: Number(row.afterQty),
      Rate:
        row.Rate !== undefined && row.Rate !== null ? Number(row.Rate) : 0,
      UserCode: userName || '',
      StrategyCode: strategyCode ? String(strategyCode) : '',
    }))

    client
      .mutate({
        mutation: UPDATE_OPEN_POSITION_FOR_CLIENT,
        variables: {
          data: payloadRows,
        },
      })
      .then((res) => {
        setDataLoading(false)
        if (res?.data?.Sp_UpdateCurrPosValue?.Status === false) {
          openNotification(
            'error',
            `An error occurred at Script ${res?.data?.Sp_UpdateCurrPosValue?.script}`
          )
        } else {
          openNotification('success', 'Updated position value successfully')
          setIsVerifyModalVisible(false)
          setIsAgreed(false)
          setChangedRows([])
          setEditableRows(new Set())
          setActiveEditingIndex(null)
          form.resetFields()
          getSpDataForCurrentPosition()
        }
      })
      .catch((err) => {
        setDataLoading(false)
        console.error(err)
        openNotification('error', err?.message || 'Failed to update positions')
      })
  }

  const renderAction = (record, index) => {
    if (!canEdit) return null
    return (
      <Space size='middle'>
        <div style={{ cursor: 'pointer' }} onClick={() => handleEdit(index)}>
          <img src={EditPencilIcon} alt='EditPencilIcon' />
        </div>
      </Space>
    )
  }

  const columns = [
    {
      title: 'Client Code',
      dataIndex: 'ClientCode',
      key: 'clientCode',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Script',
      dataIndex: 'Script',
      key: 'script',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Quantity',
      dataIndex: 'Qty',
      key: 'Qty',
      render: (text, record, index) => {
        const isEdited = getIsEdited(index, record)
        const isEditingRow = editableRows.has(index) || isEdited

        if (isEditingRow) {
          return (
            <Form.Item
              name={['positions', index, 'Qty']}
              initialValue={record?.Qty}
              style={{ margin: 0 }}
              rules={[
                { required: true, message: 'Quantity is required!' },
                {
                  validator: (_, value) => {
                    if (
                      value !== undefined &&
                      value !== null &&
                      value !== '' &&
                      value % 1 !== 0
                    ) {
                      return Promise.reject(
                        new Error(`Quantity cannot be in decimal!`)
                      )
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <InputNumber
                className={isEdited ? 'edited-quantity-highlight' : ''}
                onFocus={() => setActiveEditingIndex(index)}
              />
            </Form.Item>
          )
        }

        return (
          <span
            className={canEdit ? 'quantity-plain-text' : ''}
            onClick={() => {
              if (canEdit) handleEdit(index)
            }}
          >
            {record?.Qty}
          </span>
        )
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (text, record, index) => renderAction(record, index),
    },
  ]

  const verifyColumns = [
    {
      title: 'Client Code',
      dataIndex: 'ClientCode',
      key: 'ClientCode',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Script',
      dataIndex: 'Script',
      key: 'Script',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Before Quantity',
      dataIndex: 'beforeQty',
      key: 'beforeQty',
      align: 'center',
    },
    {
      title: 'After Quantity',
      dataIndex: 'afterQty',
      key: 'afterQty',
      align: 'center',
    },
  ]

  if (!isEmpty(error)) return <Page404 />

  return (
    <>
      <CustomMeta title='Update Position' />
      <div className='update_position_main_container'>
        {!loading && data?.length ? (
          <>
            <div className='update_position_title_container'>
              <div className='update_position_header' role='heading' aria-level={1}>
                Update Position
              </div>
            </div>
            <Form
              form={form}
              initialValues={{ positions: data }}
              onFinish={handleMainSubmit}
              onValuesChange={handleValuesChange}
            >
              <Table
                dataSource={data}
                columns={columns}
                pagination={false}
                rowKey={(record, index) =>
                  `${record?.ClientCode || ''}_${record?.Script || ''}_${index}`
                }
                rowClassName={(record, index) =>
                  activeEditingIndex === index ? 'active-editing-row' : ''
                }
              />
              {canEdit && (
                <div className='update_Position_Submit_Button_Container'>
                  <Button
                    type='primary'
                    className='updatePositionSubmitButton'
                    htmlType='submit'
                    loading={dataLoading}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </Form>

            <Modal
              visible={isVerifyModalVisible}
              onCancel={handleCancelVerification}
              footer={null}
              centered
              width={680}
              maskClosable={false}
              className='verify_position_modal'
            >
              <div className='verify_modal_header'>
                <div className='verify_modal_title'>
                  Verify Updated Positions
                </div>
                <div className='verify_modal_subtitle'>
                  Please review the changes before confirming.
                </div>
              </div>

              <Table
                className='verify_modal_table'
                dataSource={changedRows}
                columns={verifyColumns}
                pagination={false}
                rowKey={(record, index) =>
                  `${record?.ClientCode || ''}_${record?.Script || ''}_${index}`
                }
              />

              <div className='verify_declaration_container'>
                <Checkbox
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                  className='verify_declaration_checkbox'
                >
                  <span className='verify_declaration_text'>
                    I/We hereby declare that the details furnished above are
                    true and correct to the best of my/our knowledge and belief
                    and I/We undertake to inform you of any changes therein,
                    immediately. In case any of the above information is found
                    to be false or untrue or misleading or misrepresenting, I
                    am/We are aware that I/We may be held liable for it.
                  </span>
                </Checkbox>
              </div>

              <div className='verify_modal_footer'>
                <Button
                  className='verify_cancel_btn'
                  onClick={handleCancelVerification}
                  disabled={dataLoading}
                >
                  Cancel
                </Button>
                <Button
                  className='verify_submit_btn'
                  type='primary'
                  disabled={!isAgreed}
                  loading={dataLoading}
                  onClick={handleConfirmUpdate}
                >
                  Submit
                </Button>
              </div>
            </Modal>
          </>
        ) : loading ? (
          <Loader />
        ) : (
          <div className='no_position_found'>No Position Found</div>
        )}
      </div>
    </>
  )
}

export default UpdatePosition

